/**
 * /api/launches のキャッシュ判定まわりの純粋関数（Worker/DOM非依存＝テスト可能）。
 *
 * 背景（2026-07-31の障害）:
 *  LL2の匿名アクセスは「15回/時・IP単位」。Cloudflareのエッジは**他のCF利用者と共有のIP**から
 *  外部へ出るため、この枠は恒常的に枯れており、本番からのLL2取得はほぼ常に429になる。
 *  それ自体は避けようがないので、「取得できない時間が続いてもカレンダーが空にならない」ことを
 *  設計の第一条件にする。
 */
import type { LaunchDTO } from '../types';

/** 打ち上げ済み扱いにするまでの猶予。延期・当日中の表示を保つため net から24時間は残す。 */
export const PAST_GRACE_MS = 24 * 3600 * 1000;

/** cachedAt からの経過が freshSeconds 未満か（未来日時の壊れた値は「新鮮でない」とする）。 */
export function isFresh(cachedAt: string, nowMs: number, freshSeconds: number): boolean {
  const t = Date.parse(cachedAt || '');
  if (!Number.isFinite(t)) return false;
  const ageSec = (nowMs - t) / 1000;
  return ageSec >= 0 && ageSec < freshSeconds;
}

/**
 * 利用者に「この情報は古い」と伝え始めるまでの猶予（控えめな注記／警告の2段）。
 *
 * ⚠️ functions/api/launches.ts の FRESH_SECONDS(=1時間) と混同しないこと。
 *    あちらは「裏で取り直すか」の判定で、利用者への表示とは無関係。
 *    2026-07-31まではこの1つの値が両方を兼ねていたため、KVが1時間更新されないだけで
 *    トップに「最新の打ち上げ情報を取得できず」の警告が**恒常表示**されていた
 *    （実測: 有効な12件を表示できているのに警告。上記のとおりLL2はper-IP制限で
 *    CFエッジからの再取得がほぼ常に失敗するので、この警告は一過性にならない）。
 *
 * 段を分ける根拠:
 *  - 打ち上げ日程は時間単位では動かない。各運用機関の発表は通常数日前で、
 *    キャッシュが数時間古いことによる表示差はまず生じない。壊れていないものを
 *    「取得できません」と見せるのは、利用者の信用を減らすだけで得るものがない。
 *  - 一方で古いものを新鮮だと偽ってはいけない。取得時刻は一覧の見出し文に
 *    「（…時点）」として常に出しているので、黙っても取得時刻自体は隠れない
 *    （src/lib/launch-summary.ts の renderScheduleTableHtml）。
 *  - よって「黙る／控えめに言う／警告する」の3段にする。
 *
 * ⚠️ この2つの値は技術判断ではなく「どこから利用者に古いと言うか」という商品判断。
 *    変えたくなったらここだけを触ればよい（呼び出し側に閾値を書かないこと）。
 */
export const SOFT_STALE_SECONDS = 24 * 3600;
export const HARD_STALE_SECONDS = 72 * 3600;

/** current=黙る / aging=最終更新を控えめに添える / old=警告する */
export type CacheAgeLevel = 'current' | 'aging' | 'old';

export function cacheAgeLevel(
  cachedAt: string,
  nowMs: number,
  softSeconds = SOFT_STALE_SECONDS,
  hardSeconds = HARD_STALE_SECONDS,
): CacheAgeLevel {
  const t = Date.parse(cachedAt || '');
  // 齢が読めない＝新鮮だと言い切れない。安全側（警告）に倒す。
  if (!Number.isFinite(t)) return 'old';
  const ageSec = (nowMs - t) / 1000;
  // 未来日時の実際の原因は「閲覧端末の時計がずれている」で、データが古い証拠ではない。
  // isFresh は取り直しに倒すため false を返すが、それを利用者向け警告に流用すると
  // 時計のずれた端末にだけ障害を装うことになるので、ここでは current にする。
  if (ageSec < 0) return 'current';
  if (ageSec >= hardSeconds) return 'old';
  if (ageSec >= softSeconds) return 'aging';
  return 'current';
}

/**
 * 過ぎ去った回だけを落とす。
 *
 * ⚠️ 日程未定の回を落とさないことが本関数の目的。LL2は日付が決まっていない回に
 * 「その年の12/31」「その月の月末」といった仮の net を入れてくる（net_precision=Y/Q/M）。
 * ここで確度を見て弾いてしまうと『日程が決まっていない打ち上げ』が消えるので、
 * 判定は net が過ぎたかどうかだけで行う。net が読めない回は未定として残す。
 */
export function pruneStaleLaunches(launches: LaunchDTO[], nowMs: number): LaunchDTO[] {
  return launches.filter((l) => {
    const t = Date.parse(l?.net || '');
    if (!Number.isFinite(t)) return true;
    return t >= nowMs - PAST_GRACE_MS;
  });
}

/**
 * LL2の429から「次に叩いてよくなるまでの秒数」を読む。
 * 本文例: {"detail":"Request was throttled. Expected available in 1978 seconds."}
 * 読めなければ fallbackSeconds。上限は maxSeconds でクランプする。
 */
export function parseThrottleWaitSeconds(
  body: string,
  retryAfter: string | null,
  fallbackSeconds: number,
  maxSeconds: number,
): number {
  let secs = NaN;
  const m = /available in (\d+(?:\.\d+)?) seconds?/i.exec(body || '');
  if (m) secs = Number(m[1]);
  if (!Number.isFinite(secs) && retryAfter) {
    const ra = Number(retryAfter);
    if (Number.isFinite(ra)) secs = ra;
  }
  if (!Number.isFinite(secs) || secs < 0) secs = fallbackSeconds;
  return Math.min(Math.max(Math.ceil(secs), 1), maxSeconds);
}

/** バックオフ中か（KVに入れた「この時刻まで叩かない」ISO文字列を判定）。 */
export function isBackingOff(untilIso: string | null, nowMs: number): boolean {
  if (!untilIso) return false;
  const t = Date.parse(untilIso);
  return Number.isFinite(t) && t > nowMs;
}
